import { FocusEventHandler, useRef, useState } from "react";

import { useClickAway } from "ahooks";
import dropRight from "lodash/dropRight";
import uniqWith from "lodash/uniqWith";
import SelectOption from "./components/SelectOption/SelectOption";
import classNames from "classnames";
import i18n from "@breward/i18n";
import { useResponsive } from "ahooks";
import { sortBy } from "lodash";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../Tooltip/Tooltip";


export interface SelectOptionItem {
  label: string;
  value: string | number;
  prefix?: JSX.Element;
}

interface SelectProps {
  id?: string;
  label?: string | JSX.Element;
  options: SelectOptionItem[] | [];
  selected?: string | number | string[] | number[];
  disabled?: boolean;
  multi?: boolean;
  creatable?: boolean;
  onChange?: (value: string | number | string[] | number[]) => void;
  onBlur?: FocusEventHandler;
  error?: string | JSX.Element;
  placeholder?: string | JSX.Element;
  className?: string;
  sorted?: boolean;
  required?: boolean;
  tooltip?: {
    content: string;
    icon?: string;
  };
  "aria-label"?: string;
  "data-test-id"?: string;
}

export const Select = ({
  id,
  label,
  options,
  selected,
  multi = false,
  creatable = false,
  disabled = false,
  onChange,
  onBlur,
  error,
  placeholder = i18n.t("global:shared.select", "Select..."),
  className,
  sorted = true,
  required = false,
  tooltip,
  ...props
}: SelectProps) => {
  const responsive = useResponsive();
  const ref = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const [opened, setOpened] = useState(false);
  const [focusedOption, setFocusedOption] = useState<SelectOptionItem>();
  const [filter, setFilter] = useState("");

  useClickAway(() => {
    setOpened(false);
    setFilter("");
  }, ref);

  const isSelected = (value: string) => {
    if (multi) {
      return (selected as string[])?.includes(value as string);
    } else {
      return selected === value;
    }
  };

  const chooseMulti = (option: SelectOptionItem) => {
    if ((selected as string[])?.includes(option.value as string)) {
      onChange &&
        onChange(
          (selected as string[])?.filter(
            (chosenOption) => chosenOption !== option.value,
          ),
        );
    } else {
      onChange &&
        onChange([...((selected || []) as string[]), option.value as string]);
    }
  };

  const chooseSingle = (option: SelectOptionItem) => {
    if (selected === option.value) {
      // @ts-ignore
      onChange && onChange(undefined);
    } else {
      onChange && onChange(option.value);
    }
  };

  const filterOptions = (option: SelectOptionItem) => {
    if (filter?.length) {
      return !!option?.label?.match(new RegExp(`${filter}.*`, "i"));
    } else {
      return true;
    }
  };

  const handleOptionClick = (
    e: React.MouseEvent<HTMLDivElement>,
    option: SelectOptionItem,
  ) => {
    if (multi) {
      chooseMulti(option);
    } else {
      chooseSingle(option);
      setFilter("");
      setOpened(false);
    }

    e.stopPropagation();
  };

  // @ts-ignore
  const handleOptionsFilter = (e) => {
    if (
      (e.keyCode >= 48 && e.keyCode <= 111) ||
      e.keyCode === 32 ||
      (e.keyCode >= 187 && e.keyCode < 229)
    ) {
      setFilter((filter) => filter + e.key);
    } else if (e.keyCode === 8) {
      // @ts-ignore
      setFilter((filter) => dropRight(filter)?.join(""));
    } else if (e.keyCode === 38) {
      const opts = options?.filter(filterOptions);

      if (focusedOption) {
        setFocusedOption(
          opts?.[opts?.findIndex((o) => o?.value === focusedOption?.value) - 1],
        );
      } else {
        setFocusedOption(opts?.[0]);
      }
    } else if (e.keyCode === 40) {
      const opts = options?.filter(filterOptions);
      if (focusedOption) {
        setFocusedOption(
          opts?.[opts?.findIndex((o) => o?.value === focusedOption?.value) + 1],
        );
      } else {
        setFocusedOption(opts?.[0]);
      }
    } else if (e.keyCode === 13) {
      if (focusedOption) {
        if (multi) {
          chooseMulti(focusedOption);
        } else {
          onChange(focusedOption.value);
          setOpened(false);
        }
      } else {
        const mostSimilar = options?.filter(filterOptions)?.[0];

        if (mostSimilar) {
          if (multi) {
            chooseMulti(mostSimilar);
          } else {
            onChange(mostSimilar.value);
            setOpened(false);
          }
        } else if (creatable) {
          if (multi) {
            chooseMulti({ label: filter, value: filter });
          } else {
            onChange(filter);
            setOpened(false);
          }
        }
      }
      setFilter("");
    }
  };

  // @ts-ignore
  const handleMobileOptionsFilter = (e) => {
    if (e?.keyCode === 8 && e?.type === "keydown") {
      setFilter((filter) => {
        const newValue = dropRight(filter)?.join("");
        textInputRef.current.value = newValue;
      });
    } else if (e?.keyCode !== 8 && e?.type === "input") {
      setFilter(e?.target?.value?.split("")?.reverse()?.join(""));
    }
  };

  const inputValue = () => {
    if (multi && Array.isArray(selected) && (selected as string[])?.length) {
      return uniqWith(
        options
          // @ts-ignore
          .filter((option) => selected.includes(option.value.toString()))
          .concat(
            (selected as string[])?.map(
              (value) => ({ label: value, value: value }) as SelectOptionItem,
            ),
          ),
        (opt1: SelectOptionItem, opt2: SelectOptionItem) =>
          opt1.value === opt2.value,
      ).map((option: SelectOptionItem) => {
        return (
          <span
            key={option?.label}
            className="inline-flex items-center rounded-full bg-success-100 px-2.5 py-0.5 text-xs font-medium text-success-800 dark:bg-success-500/15 dark:text-success-200"
          >
            {option.label}
            <div
              onClick={() =>
                onChange &&
                onChange(
                  (selected as string[])?.filter(
                    (chosenOption: string | number) =>
                      chosenOption !== option.value,
                  ),
                )
              }
              className="icon-[ph--x] h-4 w-4 cursor-pointer text-success-900 dark:text-success-300"
            />
          </span>
        );
      });
    } else if (options?.find((option) => option.value === selected)?.label) {
      return options?.find((option) => option.value === selected)?.label;
    } else if (
      Array.isArray(selected)
        ? selected?.length
        : selected && (selected as number) > 0
    ) {
      return selected;
    } else {
      return selected || placeholder;
    }
  };

  const sortOptions = (options) => {
    if (sorted) {
      return sortBy(options, (option) => {
        if (option?.label?.match(/^[0-9]*\..*$/)) {
          return parseInt(option?.label?.match(/^[0-9]*/));
        } else {
          return option?.label;
        }
      });
    } else {
      return options;
    }
  };

  const selectLabel = typeof label === "string" ? label : undefined;

  return (
    <div className={classNames("min-w-0 w-full font-sans", className)}>
      {label && (
        <div
          className={classNames(
            "mb-1.5 block text-sm font-medium flex items-center gap-2",
            disabled ? "opacity-50" : "opacity-100",
            "text-primary-700 dark:text-primary-300",
          )}
        >
          {label}
          {tooltip && (
            <Tooltip placement="top" trigger="hover">
              <TooltipTrigger>
                <div
                  className={classNames(
                    tooltip.icon || "icon-[ph--info]",
                    "h-4 w-4 text-primary-400 dark:text-primary-500 cursor-help",
                  )}
                />
              </TooltipTrigger>
              <TooltipContent>{tooltip.content}</TooltipContent>
            </Tooltip>
          )}
          {required && (
            <span className="ml-1 text-error-500 dark:text-error-400">*</span>
          )}
        </div>
      )}

      <div ref={ref} onKeyDown={handleOptionsFilter} className="relative min-w-0">
        <div
          {...props}
          id={id}
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={opened}
          aria-label={props["aria-label"] ?? selectLabel}
          onClick={() => {
            if (!disabled) {
              setOpened(!opened);
              textInputRef?.current?.focus();
            }
          }}
          className={classNames(
            "flex min-h-10 min-w-0 flex-1 flex-row items-center gap-2 rounded-lg border border-solid py-[1px] pl-4 pr-2 font-sans text-sm transition-colors",
            "bg-gradient-to-b from-content-50 to-white dark:from-content-900 dark:to-primary-950/70",
            "text-primary-700 dark:text-content-100",
            "border-primary-200/80 dark:border-primary-800/70",
            "shadow-[inset_0_1px_2px_rgba(0,0,0,0.08)]",
            "dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.2)]",
            "hover:border-primary-300 dark:hover:border-primary-700",
            "hover:shadow-[inset_0_1px_3px_rgba(0,0,0,0.12)]",
            "dark:hover:shadow-[inset_0_1px_3px_rgba(0,0,0,0.25)]",
            "focus:border-primary-400 dark:focus:border-primary-400",
            "focus:shadow-[inset_0_1px_2px_rgba(0,0,0,0.08),0_0_0_3px_rgba(167,80,53,0.16)]",
            "dark:focus:shadow-[inset_0_1px_2px_rgba(0,0,0,0.2),0_0_0_3px_rgba(255,182,137,0.22)]",
            "focus:outline-none",
            disabled &&
              "cursor-not-allowed opacity-50 bg-primary-50/50 dark:bg-primary-900/30 hover:shadow-none dark:hover:shadow-none",
            error &&
              "border-error-300 dark:border-error-700 shadow-[inset_0_1px_2px_rgba(220,38,38,0.1)] dark:shadow-[inset_0_1px_2px_rgba(220,38,38,0.2)]",
          )}
        >
          <span
            className={classNames(
              "block flex-1 truncate",
              disabled
                ? "text-primary-400 dark:text-primary-600"
                : selected
                  ? "text-primary-700 dark:text-content-100"
                  : "text-primary-400 dark:text-content-500",
            )}
          >
            {inputValue() || placeholder}
          </span>

          <div
            className={classNames(
              "icon-[ph--caret-down] h-4 w-4 shrink-0 transition-transform",
              opened && "rotate-180",
              disabled
                ? "text-primary-300 dark:text-primary-600"
                : error
                  ? "text-error-500 dark:text-error-400"
                  : "text-primary-400 dark:text-primary-500",
            )}
          />
        </div>

        {error && (
          <p className="mt-1.5 text-sm font-medium text-error-600 dark:text-error-400">
            {error}
          </p>
        )}

        {opened && filter && (
          <div className="absolute right-0 top-0 mr-8 mt-2 text-xs">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-content-100 px-2 py-0.5 text-content-600 dark:bg-content-800 dark:text-content-300">
              {i18n.t("global:shared.Search", "Search")}: {filter}
              <button
                onClick={() => {
                  setFilter("");
                  textInputRef.current.value = "";
                }}
                className="icon-[ph--x] h-3.5 w-3.5 text-content-400 hover:text-content-500 dark:text-content-500 dark:hover:text-content-400"
              />
            </div>
          </div>
        )}

        <>
          {opened && (
            <div
              role="listbox"
              aria-label={props["aria-label"] ?? selectLabel}
              className={classNames(
                "z-[100] mt-1 max-h-64 w-full overflow-auto rounded-lg border py-1",
                "bg-white dark:bg-content-900",
                "border-content-300 dark:border-primary-800/70",
                "shadow-lg shadow-content-900/5 dark:shadow-content-900/20",
                "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-content-200 dark:scrollbar-thumb-content-700",
              )}
              style={{
                width: ref.current?.clientWidth + "px",
                left: ref.current?.getBoundingClientRect().left + "px",
                top: ref.current?.getBoundingClientRect().bottom + "px",
              }}
            >
              {options?.filter(filterOptions)?.length ? (
                sortOptions(options?.filter(filterOptions))?.map(
                  (option, optionIdx) => (
                    <SelectOption
                      key={option.value}
                      id={`select-option-${optionIdx}`}
                      dataCy={`select-option-${optionIdx}`}
                      option={option}
                      isSelected={isSelected(option.value)}
                      isFocused={focusedOption?.value === option.value}
                      onClick={(e) => handleOptionClick(e, option)}
                    />
                  ),
                )
              ) : creatable ? (
                <div
                  onClick={() => {
                    if (multi) {
                      chooseMulti({ label: filter, value: filter });
                    } else {
                      onChange(filter);
                      setOpened(false);
                    }
                  }}
                  className={classNames(
                    "flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition",
                    "hover:bg-content-50 dark:hover:bg-content-700/50",
                  )}
                >
                  <div className="icon-[ph--plus] h-4 w-4 text-primary-500 dark:text-primary-400" />
                  <span className="text-primary-600 dark:text-primary-300">
                    {i18n.t("global:shared.addNewOption", "Add")}:{" "}
                    <b>{filter}</b>
                  </span>
                </div>
              ) : (
                <div className="px-3 py-2 text-sm text-content-400 dark:text-content-500">
                  {i18n.t("global:shared.noResults", "No results found")}
                </div>
              )}
            </div>
          )}
        </>
      </div>
    </div>
  );
};

Select.displayName = "Select";
